import { useMemo, useState } from 'react'
import { formatMoney } from '../lib/format.js'

// Hand-rolled SVG scatter for menu engineering.
// X axis: units sold over 90 days (normalised to max).
// Y axis: contribution margin per unit (normalised to max).
// Threshold lines = restaurant-relative averages from the view.
// Dot colour = quadrant tone (matches the 4-grid below).
//
// No charting deps. ~200 LOC including a state-driven tooltip and
// deterministic jitter for overlap mitigation.

const VIEWBOX = 320
const MARGIN_L = 28
const MARGIN_B = 28
const MARGIN_T = 12
const MARGIN_R = 12
const PLOT_W = VIEWBOX - MARGIN_L - MARGIN_R
const PLOT_H = VIEWBOX - MARGIN_T - MARGIN_B

// Same palette as MenuEngineeringCard.TONES, expressed as raw colours
// because SVG doesn't speak Tailwind class names.
const DOT_COLORS = {
  star:      '#10b981', // emerald-500
  plowhorse: '#f59e0b', // amber-500
  puzzle:    '#3b82f6', // blue-500
  dog:       '#ef4444', // red-500
}

// Cheap deterministic hash → -1..1 range for ±2px jitter.
function jitterFromId(id) {
  if (!id) return [0, 0]
  let h = 2166136261
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  const a = ((h & 0xffff) / 0xffff) * 2 - 1
  const b = (((h >>> 16) & 0xffff) / 0xffff) * 2 - 1
  return [a * 2, b * 2]
}

export default function MenuEngineeringScatter({
  rows,
  thresholds,
  activeQuadrant = null,
  // --- OPTIONAL group-mode props. When absent, output is byte-identical
  //     to the single-unit behavior. ---
  // colorBy: 'classification' (default, quadrant tones) | 'unit' (dot
  //   fill comes from unitColorFor(restaurant_id)).
  colorBy = 'classification',
  // unitColorFor: (restaurant_id) => hex color. Required when
  //   colorBy === 'unit'; ignored otherwise.
  unitColorFor = null,
  // unitNameById: Map<restaurant_id, name>. When present, the tooltip +
  //   <title> append the unit name line (multi-unit context).
  unitNameById = undefined,
}) {
  const [hover, setHover] = useState(null)

  const { plotted, excluded, maxUnits, maxCm } = useMemo(() => {
    const valid = []
    const dropped = []
    for (const r of rows || []) {
      const u = Number(r.units_90d || 0)
      const cm = Number(r.cm_per_unit || 0)
      if (u === 0 || cm <= 0) {
        dropped.push(r)
      } else {
        valid.push(r)
      }
    }
    const mu = valid.reduce((m, r) => Math.max(m, Number(r.units_90d || 0)), 0)
    const mc = valid.reduce((m, r) => Math.max(m, Number(r.cm_per_unit || 0)), 0)
    return {
      plotted: valid,
      excluded: dropped,
      maxUnits: mu > 0 ? mu : 1,
      maxCm:    mc > 0 ? mc : 1,
    }
  }, [rows])

  if (!plotted.length && !excluded.length) return null

  const avgU = Number(thresholds?.avg_units || 0)
  const avgC = Number(thresholds?.avg_cm || 0)
  const avgUx = MARGIN_L + (avgU / maxUnits) * PLOT_W
  const avgCy = MARGIN_T + PLOT_H - (avgC / maxCm) * PLOT_H

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
        className="aspect-square w-full"
        role="img"
        aria-label="Menu engineering scatter chart: units sold vs contribution margin per unit"
      >
        {/* Tap-outside background — clears the tooltip */}
        <rect
          x="0" y="0" width={VIEWBOX} height={VIEWBOX}
          fill="transparent"
          onClick={() => setHover(null)}
        />

        {/* Plot frame */}
        <rect
          x={MARGIN_L} y={MARGIN_T}
          width={PLOT_W} height={PLOT_H}
          fill="white"
          stroke="rgba(0,0,0,0.08)"
          strokeWidth="1"
        />

        {/* Threshold lines */}
        {avgU > 0 && (
          <>
            <line
              x1={avgUx} y1={MARGIN_T}
              x2={avgUx} y2={MARGIN_T + PLOT_H}
              stroke="rgba(0,0,0,0.2)"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
            <text
              x={avgUx + 3} y={MARGIN_T + 10}
              fontSize="8"
              fill="rgba(0,0,0,0.5)"
            >
              avg units
            </text>
          </>
        )}
        {avgC > 0 && (
          <>
            <line
              x1={MARGIN_L}        y1={avgCy}
              x2={MARGIN_L + PLOT_W} y2={avgCy}
              stroke="rgba(0,0,0,0.2)"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
            <text
              x={MARGIN_L + PLOT_W - 30} y={avgCy - 3}
              fontSize="8"
              fill="rgba(0,0,0,0.5)"
            >
              avg CM
            </text>
          </>
        )}

        {/* Axis labels */}
        <text
          x={MARGIN_L + PLOT_W / 2}
          y={VIEWBOX - 6}
          fontSize="10"
          textAnchor="middle"
          fill="rgba(0,0,0,0.6)"
        >
          units sold (90d) →
        </text>
        <text
          x={-(MARGIN_T + PLOT_H / 2)}
          y={10}
          fontSize="10"
          textAnchor="middle"
          transform="rotate(-90)"
          fill="rgba(0,0,0,0.6)"
        >
          CM / unit →
        </text>

        {/* Dots */}
        {plotted.map((r) => {
          const u = Number(r.units_90d || 0)
          const cm = Number(r.cm_per_unit || 0)
          const [jx, jy] = jitterFromId(r.item_id)
          const cx = MARGIN_L + (u / maxUnits) * PLOT_W + jx
          const cy = MARGIN_T + PLOT_H - (cm / maxCm) * PLOT_H + jy
          // Unit-color mode (group scatter) when colorBy==='unit' and a
          // resolver is supplied; otherwise the classic quadrant tone.
          const fill =
            colorBy === 'unit' && typeof unitColorFor === 'function'
              ? (unitColorFor(r.restaurant_id) || '#6b7280')
              : (DOT_COLORS[r.classification] || '#6b7280')
          const unitName = unitNameById ? unitNameById.get(r.restaurant_id) : undefined
          // Quadrant filter dims off-bucket dots but keeps them on
          // screen so the manager doesn't lose context of where the
          // selected quadrant sits relative to the rest of the menu.
          const dotOpacity = activeQuadrant && r.classification !== activeQuadrant ? 0.3 : 1
          return (
            <circle
              key={r.item_id}
              cx={cx} cy={cy} r={5}
              fill={fill}
              fillOpacity={dotOpacity}
              stroke="white"
              strokeWidth="1.5"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation()
                setHover({ row: r, x: cx, y: cy })
              }}
              onMouseEnter={() => setHover({ row: r, x: cx, y: cy })}
              onMouseLeave={() => setHover((h) => (h?.row?.item_id === r.item_id ? null : h))}
            >
              <title>
                {r.item_name}{unitName ? ` (${unitName})` : ''} — {Math.round(u)}u · {formatMoney(cm)} CM/unit
              </title>
            </circle>
          )
        })}
      </svg>

      {hover && (
        <div
          className="pointer-events-none absolute z-10 max-w-[200px] rounded-md bg-black/85 px-2 py-1.5 text-xs leading-tight text-white shadow-lg"
          style={{
            left:  `${(hover.x / VIEWBOX) * 100}%`,
            top:   `${(hover.y / VIEWBOX) * 100}%`,
            transform: 'translate(-50%, calc(-100% - 8px))',
          }}
        >
          <div className="font-semibold">{hover.row.item_name}</div>
          {unitNameById && unitNameById.get(hover.row.restaurant_id) && (
            <div className="opacity-75">{unitNameById.get(hover.row.restaurant_id)}</div>
          )}
          <div className="tabular-nums opacity-85">
            {Math.round(Number(hover.row.units_90d || 0))}u · {formatMoney(Number(hover.row.cm_per_unit || 0))}/unit
          </div>
        </div>
      )}

      {excluded.length > 0 && (
        <div className="mt-1 text-[10px] text-black/50">
          ({excluded.length} {excluded.length === 1 ? 'item' : 'items'} with no recent sales — see grid below)
        </div>
      )}
    </div>
  )
}
